import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { CodeableConcept, Coding, Encounter } from '@medplum/fhirtypes';
import { subMonths } from 'date-fns';

export type AcuteEncounterRecord = WithId<Encounter>;

export type AcuteEncounterKind = 'edVisit' | 'admission';

/** Where the patient went at the end of the episode, reduced to what the UI distinguishes. */
export type AcuteDisposition =
  'home' | 'facility' | 'leftAgainstAdvice' | 'leftBeforeSeen' | 'admitted' | 'other' | 'unrecorded';

export type AcuteEncounterClassification = {
  kind: AcuteEncounterKind;
  /** The row carries an emergency-department marker (EMER class, Epic type 212, ED CPT or ED type text). */
  emergencyMarker: boolean;
  /** The source filed the row as inpatient; it shows as an ED visit because it was short and ended at home. */
  demotedFromInpatient: boolean;
  /** Under two hours, `status: unknown`, no disposition: only meaningful inside a known stay. */
  weakInpatientRow: boolean;
};

export type AcuteEpisode = {
  kind: AcuteEncounterKind;
  /** The row that best represents the episode; detail views open on it. */
  encounter: AcuteEncounterRecord;
  /** Every source row in the episode, earliest first. Includes `encounter`. */
  encounters: AcuteEncounterRecord[];
  /** Exact re-imports of rows in `encounters`, dropped by shared source identifier. */
  duplicates: AcuteEncounterRecord[];
  /** ED rows that ended within a day before an admission and are shown as its arrival. Admissions only. */
  emergencyEncounters: AcuteEncounterRecord[];
  start: string;
  end: string | undefined;
  /** `false` when any row in the episode has no period end: the discharge has not reached us. */
  dischargeRecorded: boolean;
  /** Calendar days between admission and discharge. Admissions with a recorded discharge only. */
  lengthOfStayDays: number | undefined;
  /** Whole minutes between start and end. Episodes with a recorded discharge only. */
  durationMinutes: number | undefined;
  /** The stay started in the emergency department. Admissions only. */
  viaEmergency: boolean;
  demotedFromInpatient: boolean;
  disposition: AcuteDisposition;
  /** Distinct reason texts across the episode's rows, in row order. */
  reasons: string[];
  /** Location or service-provider display from the rows, when any carries one. */
  facility: string | undefined;
};

export type GroupedAcuteEncounters = {
  /** Newest first by start. */
  episodes: AcuteEpisode[];
  /** Rows dropped as exact re-imports of another row. */
  duplicateCount: number;
};

export type AcuteEpisodeCounts = { edVisits: number; admissions: number };

export type AcuteEpisodeSummary = {
  lastYear: AcuteEpisodeCounts;
  allTime: AcuteEpisodeCounts;
  mostRecent: AcuteEpisode | undefined;
};

export const ACT_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ActCode';
const EPIC_ENCOUNTER_TYPE_SYSTEM = 'urn:oid:1.2.840.114350.1.72.1.30';
const EPIC_EMERGENCY_TYPE_CODE = '212';
const CPT_SYSTEM = 'http://www.ama-assn.org/go/cpt';
const ED_CPT_CODES = /^9928[1-5]$/;
const ED_TYPE_TEXT = /\bemergency\b|\be\/?r\b|\bed\b|triage/;
const INPATIENT_TYPE_TEXT = /inpatient/;
const INPATIENT_CLASS_CODES = new Set(['IMP', 'ACUTE', 'NONAC', 'OBSENC']);
const LIZA_IDENTIFIER_HOST = 'liza.health';

/**
 * Server filters that together return every row the grouper can classify: the ActCode acute classes
 * plus the eClinicalWorks `UNK` rows, and the rows whose only ED marker is the type text. Consumers
 * run both, union by id, and group once.
 */
export const ACUTE_ENCOUNTER_CLASS_SEARCH: Readonly<Record<string, string>> = {
  class: 'EMER,IMP,ACUTE,NONAC,OBSENC,UNK',
};
export const ACUTE_ENCOUNTER_TEXT_SEARCH: Readonly<Record<string, string>> = { 'type:text': 'emergency' };
export const ACUTE_ENCOUNTER_SEARCHES: readonly Readonly<Record<string, string>>[] = [
  ACUTE_ENCOUNTER_CLASS_SEARCH,
  ACUTE_ENCOUNTER_TEXT_SEARCH,
];

const DEMOTION_MAX_HOURS = 12;
const WEAK_ROW_MAX_HOURS = 2;
const ADMISSION_GAP_DAYS = 1;
const OPEN_ADMISSION_WINDOW_DAYS = 30;
const ED_MERGE_HOURS = 6;
const VIA_EMERGENCY_MAX_DAYS = 1;

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

const typeCodings = (encounter: Encounter): Coding[] => (encounter.type ?? []).flatMap((type) => type.coding ?? []);

const typeText = (encounter: Encounter): string =>
  (encounter.type ?? [])
    .flatMap((type) => [type.text ?? '', ...(type.coding ?? []).map((coding) => coding.display ?? '')])
    .join(' | ')
    .toLowerCase();

/** `UNK` is a null flavor, not a class, so a row carrying it is classified from its type text like a non-ActCode row. */
const isActCodeClass = (encounter: Encounter): boolean =>
  encounter.class?.system === ACT_CODE_SYSTEM && encounter.class.code !== 'UNK';

/** A date-only start (Care Everywhere copies) has no time to compare and no end to expect. */
const isDateOnly = (value: string): boolean => value.length <= 10;

const hasEmergencyMarker = (encounter: Encounter): boolean =>
  encounter.class?.code === 'EMER' ||
  typeCodings(encounter).some(
    (coding) =>
      (coding.system === EPIC_ENCOUNTER_TYPE_SYSTEM && coding.code === EPIC_EMERGENCY_TYPE_CODE) ||
      (coding.system === CPT_SYSTEM && ED_CPT_CODES.test(coding.code ?? '')),
  ) ||
  (!isActCodeClass(encounter) && ED_TYPE_TEXT.test(typeText(encounter)));

const hasInpatientMarker = (encounter: Encounter): boolean =>
  INPATIENT_CLASS_CODES.has(encounter.class?.code ?? '') ||
  (!isActCodeClass(encounter) && INPATIENT_TYPE_TEXT.test(typeText(encounter)));

const hoursBetween = (start: string, end: string): number => (Date.parse(end) - Date.parse(start)) / HOUR_MS;

/** Calendar days between two timestamps as written, so a UTC stamp is not shifted into the local day. */
const utcDay = (value: string): number =>
  Date.UTC(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));

const calendarDaysBetween = (start: string, end: string): number => Math.round((utcDay(end) - utcDay(start)) / DAY_MS);

const dispositionText = (encounter: Encounter): string | undefined => {
  const disposition = encounter.hospitalization?.dischargeDisposition;
  if (!disposition) return undefined;
  const text = formatCodeableConcept(disposition);
  return text || undefined;
};

const DISPOSITION_RULES: readonly [AcuteDisposition, RegExp][] = [
  ['leftAgainstAdvice', /against (medical )?advice|\bama\b|aadvice/],
  ['leftBeforeSeen', /left (before|without)|before triage|\blwbs\b|eloped/],
  ['admitted', /admit|\bhosp\b|inpatient/],
  ['facility', /facility|nursing|\bsnf\b|rehab|psych|hospice|long.?term|transfer|\bltc\b|\bother-hcf\b/],
  ['home', /\bhome\b|self.?care|routine|alt-home|discharged to residence/],
];

/** Reduce a source disposition to the display enum. Codes and text are matched together, lower-cased. */
export const normalizeDisposition = (disposition: CodeableConcept | undefined): AcuteDisposition => {
  if (!disposition) return 'unrecorded';
  const haystack = [
    disposition.text ?? '',
    ...(disposition.coding ?? []).flatMap((c) => [c.code ?? '', c.display ?? '']),
  ]
    .join(' ')
    .toLowerCase()
    .trim();
  if (!haystack) return 'unrecorded';
  return DISPOSITION_RULES.find(([, pattern]) => pattern.test(haystack))?.[0] ?? 'other';
};

const isWeakInpatientRow = (encounter: Encounter): boolean => {
  const { start, end } = encounter.period ?? {};
  return (
    encounter.status === 'unknown' &&
    !dispositionText(encounter) &&
    !!start &&
    !!end &&
    hoursBetween(start, end) < WEAK_ROW_MAX_HOURS
  );
};

/**
 * Decide whether one Encounter describes an ED visit or a hospital admission. `undefined` for every
 * other row, which stays in the plain encounter list. A row with no period start cannot be placed
 * on a timeline and is also `undefined`.
 */
export const classifyAcuteEncounter = (encounter: Encounter): AcuteEncounterClassification | undefined => {
  const start = encounter.period?.start;
  if (!start) return undefined;

  const emergencyMarker = hasEmergencyMarker(encounter);
  if (hasInpatientMarker(encounter)) {
    const end = encounter.period?.end;
    const disposition = normalizeDisposition(encounter.hospitalization?.dischargeDisposition);
    const endedAtHome = disposition === 'home' || disposition === 'unrecorded';
    // No end means the discharge has not reached us, never that the stay was short.
    const shortAndHome = !!end && hoursBetween(start, end) < DEMOTION_MAX_HOURS && endedAtHome;
    if (emergencyMarker && shortAndHome) {
      return { kind: 'edVisit', emergencyMarker, demotedFromInpatient: true, weakInpatientRow: false };
    }
    return {
      kind: 'admission',
      emergencyMarker,
      demotedFromInpatient: false,
      weakInpatientRow: isWeakInpatientRow(encounter),
    };
  }
  if (emergencyMarker) {
    return { kind: 'edVisit', emergencyMarker, demotedFromInpatient: false, weakInpatientRow: false };
  }
  return undefined;
};

type Row = {
  encounter: AcuteEncounterRecord;
  classification: AcuteEncounterClassification;
  start: string;
  end: string | undefined;
  duplicates: AcuteEncounterRecord[];
};

const sourceIdentifierKeys = (encounter: Encounter): string[] =>
  (encounter.identifier ?? [])
    .filter((identifier) => identifier.system && identifier.value && !identifier.system.includes(LIZA_IDENTIFIER_HOST))
    .map((identifier) => `${identifier.system}|${identifier.value}`);

const spanHours = (row: Row): number => (row.end ? hoursBetween(row.start, row.end) : 0);

/**
 * Which of two rows describing the same thing should represent it: an ActCode-classed row with an
 * end, then one with a disposition, then one with reasons, then the earlier start, then id so the
 * result is deterministic.
 */
const compareDisplayPreference = (a: Row, b: Row): number =>
  Number(!!b.end && isActCodeClass(b.encounter)) - Number(!!a.end && isActCodeClass(a.encounter)) ||
  Number(!!b.end) - Number(!!a.end) ||
  Number(!!dispositionText(b.encounter)) - Number(!!dispositionText(a.encounter)) ||
  (b.encounter.reasonCode?.length ?? 0) - (a.encounter.reasonCode?.length ?? 0) ||
  a.start.localeCompare(b.start) ||
  a.encounter.id.localeCompare(b.encounter.id);

/** A timed row with no end awaits its discharge; date-only copies never carry one. */
const awaitsDischarge = (row: Row): boolean => !row.end && !isDateOnly(row.start);

/** The row sits inside another row's recorded period, so its missing end is an unclosed order, not the stay's. */
const isNestedIn = (row: Row, rows: Row[]): boolean =>
  rows.some((other) => other !== row && !!other.end && other.start <= row.start && other.end >= row.start);

/** Rows whose missing end means the episode itself has no discharge yet. */
const trailingOpenRows = (rows: Row[]): Set<Row> =>
  new Set(rows.filter((row) => awaitsDischarge(row) && !isNestedIn(row, rows)));

/**
 * For a stay the main admission row is the one still awaiting discharge, else the longest;
 * sub-encounters are short.
 */
const compareAdmissionDisplayPreference =
  (trailing: Set<Row>) =>
  (a: Row, b: Row): number =>
    Number(trailing.has(b)) - Number(trailing.has(a)) || spanHours(b) - spanHours(a) || compareDisplayPreference(a, b);

/**
 * Collapse exact re-imports: rows sharing any non-Liza identifier are one row, joined transitively
 * through any chain of shared identifiers. The preferred display row survives and the rest hang off
 * it as `duplicates`.
 */
const collapseDuplicates = (rows: Row[]): Row[] => {
  const parent = rows.map((_, index) => index);
  const find = (index: number): number => {
    let node = index;
    for (let next = parent[node]; next !== undefined && next !== node; next = parent[node]) node = next;
    return node;
  };
  const firstByKey = new Map<string, number>();
  rows.forEach((row, index) => {
    for (const key of sourceIdentifierKeys(row.encounter)) {
      const seen = firstByKey.get(key);
      if (seen === undefined) firstByKey.set(key, index);
      else parent[find(index)] = find(seen);
    }
  });

  const groups = new Map<number, Row[]>();
  rows.forEach((row, index) => {
    const root = find(index);
    const group = groups.get(root);
    if (group) group.push(row);
    else groups.set(root, [row]);
  });

  return [...groups.values()].map((group) => {
    const display = group.reduce((best, row) => (compareDisplayPreference(row, best) < 0 ? row : best));
    display.duplicates.push(...group.filter((row) => row !== display).map((row) => row.encounter));
    return display;
  });
};

type Draft = {
  kind: AcuteEncounterKind;
  rows: Row[];
  start: string;
  end: string | undefined;
  emergencyRows: Row[];
};

/** A row awaiting its discharge, and not nested inside a closed row, leaves the whole episode open. */
const isOpen = (draft: Draft): boolean => trailingOpenRows(draft.rows).size > 0;

const openDraft = (kind: AcuteEncounterKind, row: Row): Draft => ({
  kind,
  rows: [row],
  start: row.start,
  end: row.end,
  emergencyRows: [],
});

const absorb = (draft: Draft, row: Row): void => {
  draft.rows.push(row);
  if (row.end && (!draft.end || row.end > draft.end)) draft.end = row.end;
};

const byStart = (a: Row, b: Row): number => a.start.localeCompare(b.start) || compareDisplayPreference(a, b);

const buildAdmissions = (rows: Row[]): Draft[] => {
  const drafts: Draft[] = [];
  for (const row of rows.sort(byStart)) {
    const last = drafts.at(-1);
    // A known discharge lets a 1-day gap join sub-encounters. A stay awaiting its discharge has no end
    // to measure from, so it absorbs rows for 30 days after admission instead.
    const joins =
      !!last &&
      (isOpen(last)
        ? calendarDaysBetween(last.start, row.start) <= OPEN_ADMISSION_WINDOW_DAYS
        : calendarDaysBetween(last.end ?? last.start, row.start) <= ADMISSION_GAP_DAYS);
    if (last && joins) {
      absorb(last, row);
    } else if (!row.classification.weakInpatientRow) {
      drafts.push(openDraft('admission', row));
    }
  }
  return drafts;
};

const buildEdVisits = (rows: Row[]): Draft[] => {
  const drafts: Draft[] = [];
  for (const row of rows.sort(byStart)) {
    const last = drafts.at(-1);
    const overlaps = !!last?.end && row.start <= last.end;
    // A date-only copy has no time of day to compare, so it joins any visit on the same day.
    const closeStarts =
      !!last &&
      (isDateOnly(last.start) || isDateOnly(row.start)
        ? calendarDaysBetween(last.start, row.start) === 0
        : hoursBetween(last.start, row.start) <= ED_MERGE_HOURS);
    if (last && (overlaps || closeStarts)) {
      absorb(last, row);
    } else {
      drafts.push(openDraft('edVisit', row));
    }
  }
  return drafts;
};

const attachEdArrivals = (edVisits: Draft[], admissions: Draft[]): void => {
  for (const visit of edVisits) {
    const arrivalEnd = visit.end ?? visit.start;
    // The visit began before the stay and ended at most a day before it (or overlaps it). The start
    // bound keeps an ED row with an absurd end from attaching to a stay days later.
    const admission = admissions.find(
      (candidate) =>
        visit.start <= candidate.start &&
        calendarDaysBetween(arrivalEnd, candidate.start) <= VIA_EMERGENCY_MAX_DAYS &&
        calendarDaysBetween(visit.start, candidate.start) <= VIA_EMERGENCY_MAX_DAYS + 1,
    );
    if (admission) admission.emergencyRows.push(...visit.rows);
  }
};

const uniqueTexts = (values: (string | undefined)[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
};

const facilityOf = (encounters: Encounter[]): string | undefined =>
  encounters
    .flatMap((encounter) => [
      ...(encounter.location ?? []).map((entry) => entry.location?.display),
      encounter.serviceProvider?.display,
    ])
    .find((display) => !!display?.trim());

const toEpisode = (draft: Draft): AcuteEpisode => {
  const trailing = trailingOpenRows(draft.rows);
  const prefer = draft.kind === 'admission' ? compareAdmissionDisplayPreference(trailing) : compareDisplayPreference;
  const display = draft.rows.reduce((best, row) => (prefer(row, best) < 0 ? row : best));
  const encounters = draft.rows.map((row) => row.encounter);
  const end = trailing.size > 0 ? undefined : draft.end;
  const dispositionSource = draft.rows.find((row) => dispositionText(row.encounter))?.encounter;
  return {
    kind: draft.kind,
    encounter: display.encounter,
    encounters,
    duplicates: draft.rows.flatMap((row) => row.duplicates),
    emergencyEncounters: draft.emergencyRows.map((row) => row.encounter),
    start: draft.start,
    end,
    dischargeRecorded: !!end,
    lengthOfStayDays: end && draft.kind === 'admission' ? calendarDaysBetween(draft.start, end) : undefined,
    durationMinutes: end ? Math.round((Date.parse(end) - Date.parse(draft.start)) / 60_000) : undefined,
    viaEmergency:
      draft.kind === 'admission' &&
      (draft.emergencyRows.length > 0 || draft.rows.some((row) => row.classification.emergencyMarker)),
    demotedFromInpatient: draft.rows.some((row) => row.classification.demotedFromInpatient),
    disposition: normalizeDisposition(dispositionSource?.hospitalization?.dischargeDisposition),
    reasons: uniqueTexts(
      encounters.flatMap((encounter) => (encounter.reasonCode ?? []).map((reason) => formatCodeableConcept(reason))),
    ),
    facility: facilityOf(encounters),
  };
};

/**
 * Build a patient's ED visits and hospital stays from the full set of acute-classed encounters.
 * Pure and order-independent; call it once on the complete result of {@link ACUTE_ENCOUNTER_SEARCHES},
 * never on a page, since episodes are assembled from neighbouring rows.
 */
export const groupAcuteEncounters = (encounters: AcuteEncounterRecord[]): GroupedAcuteEncounters => {
  const seenIds = new Set<string>();
  const rows: Row[] = [];
  for (const encounter of encounters) {
    if (seenIds.has(encounter.id)) continue;
    seenIds.add(encounter.id);
    const classification = classifyAcuteEncounter(encounter);
    if (!classification || !encounter.period?.start) continue;
    rows.push({ encounter, classification, start: encounter.period.start, end: encounter.period.end, duplicates: [] });
  }

  const unique = collapseDuplicates(rows);
  const admissions = buildAdmissions(unique.filter((row) => row.classification.kind === 'admission'));
  const edVisits = buildEdVisits(unique.filter((row) => row.classification.kind === 'edVisit'));
  attachEdArrivals(edVisits, admissions);

  const episodes = [...admissions, ...edVisits]
    .map(toEpisode)
    .sort((a, b) => b.start.localeCompare(a.start) || a.encounter.id.localeCompare(b.encounter.id));
  const duplicateCount = rows.length - unique.length;
  return { episodes, duplicateCount };
};

const countEpisodes = (episodes: AcuteEpisode[]): AcuteEpisodeCounts => ({
  edVisits: episodes.filter((episode) => episode.kind === 'edVisit').length,
  admissions: episodes.filter((episode) => episode.kind === 'admission').length,
});

/** Counts for the records overview and sidebar. `now` pins the 12-month window for tests. */
export const summarizeAcuteEpisodes = (episodes: AcuteEpisode[], now: Date = new Date()): AcuteEpisodeSummary => {
  const cutoff = subMonths(now, 12).getTime();
  return {
    lastYear: countEpisodes(episodes.filter((episode) => Date.parse(episode.start) >= cutoff)),
    allTime: countEpisodes(episodes),
    mostRecent: episodes[0],
  };
};
